/**
 * Wallet reporting endpoints for the admin interface
 */
import type { AxiosRequestConfig } from 'axios';
import type { PlatformManagementApiResponse } from '../../types/Response';
import BaseResource from '../base';
import type { RedemptionData, SalesReportingData } from '../../types/WalletReportingData';

const prefix = 'gift-wallet/admin/';

/**
 * Object containing functions to generate URLs for wallet reporting endpoints
 */
const walletReporting = {
  /**
   * Generates the URL for the sales report endpoint
   *
   * @param startDate - Optional start date for the report period in YYYY-MM-DD format
   * @param endDate - Optional end date for the report period in YYYY-MM-DD format
   * @param redeemStart - Optional start date for filtering by redemption date in YYYY-MM-DD format
   * @param redeemEnd - Optional end date for filtering by redemption date in YYYY-MM-DD format
   * @returns The fully qualified URL for the sales report endpoint
   */
  getSalesReport: (startDate?: string, endDate?: string, redeemStart?: string, redeemEnd?: string) => {
    const baseUrl = `${prefix}wallet-reporting/sales`;
    const params = [];

    if (startDate) params.push(`startDate=${startDate}`);
    if (endDate) params.push(`endDate=${endDate}`);
    if (redeemStart) params.push(`redeemStart=${redeemStart}`);
    if (redeemEnd) params.push(`redeemEnd=${redeemEnd}`);

    if (params.length > 0) {
      return `${baseUrl}?${params.join('&')}`;
    }
    return baseUrl;
  },

  /**
   * Generates the URL for the redemptions report endpoint
   *
   * @param startDate - Optional start date for the report period in YYYY-MM-DD format
   * @param endDate - Optional end date for the report period in YYYY-MM-DD format
   * @returns The fully qualified URL for the redemptions report endpoint
   */
  getRedemptionsReport: (startDate?: string, endDate?: string) => {
    let baseUrl = `${prefix}wallet-reporting/redemptions`;
    const params = [];

    if (startDate) params.push(`startDate=${startDate}`);
    if (endDate) params.push(`endDate=${endDate}`);

    if (params.length > 0) {
      baseUrl += `?${params.join('&')}`;
    }

    return baseUrl;
  },

  getStats() {
    const baseUrl = `${prefix}wallet-reporting/stats`;

    return baseUrl;
  },
};

/**
 * Resource class for wallet reporting API endpoints in the admin interface
 */
export class AdminWalletReportingResource extends BaseResource {
  /**
   * Fetches the sales report data for a specified date range
   *
   * @param startDate - Start date for the report period in YYYY-MM-DD format
   * @param endDate - End date for the report period in YYYY-MM-DD format
   * @param redeemStart - Optional start date for filtering by redemption date
   * @param redeemEnd - Optional end date for filtering by redemption date
   * @param options - Optional axios request configuration
   * @returns Promise resolving to the sales report data response
   */
  getSalesReport(
    startDate?: string,
    endDate?: string,
    redeemStart?: string,
    redeemEnd?: string,
    options?: AxiosRequestConfig
  ) {
    return this.client.get<SalesReportingData[], PlatformManagementApiResponse<SalesReportingData[]>>(
      walletReporting.getSalesReport(startDate, endDate, redeemStart, redeemEnd),
      options
    );
  }

  getStats(options?: AxiosRequestConfig) {
    return this.client.get<SalesReportingData, PlatformManagementApiResponse<SalesReportingData>>(
      walletReporting.getStats(),
      options
    );
  }
  /**
   * Fetches the redemptions report data for a specified date range
   *
   * @param startDate - Start date for the report period in YYYY-MM-DD format
   * @param endDate - End date for the report period in YYYY-MM-DD format
   * @param options - Optional axios request configuration
   * @returns Promise resolving to the redemptions report data response
   */
  getRedemptionsReport(startDate?: string, endDate?: string, options?: AxiosRequestConfig) {
    return this.client.get<RedemptionData[], PlatformManagementApiResponse<RedemptionData[]>>(
      walletReporting.getRedemptionsReport(startDate, endDate),
      options
    );
  }
}

export default walletReporting;
